import React, {
	useMemo,
	KeyboardEvent,
	Dispatch,
	SetStateAction,
	ReactNode,
} from "react";
import classnames from "classnames";

// react-select
import {
	OnChangeValue,
	ActionMeta,
	createFilter,
	CSSObjectWithLabel,
	MultiValueProps,
	GroupBase,
} from "react-select";

// react-select-async-paginate which is built on top of react-slect
import { AsyncPaginate as AsyncPaginateSelect } from "react-select-async-paginate";
import { ElemAfter, ElemBefore, Elem } from "@alm/flow-ds/components";

interface CommonFormElementProps {
	className?: string;
	dirty?: boolean;
	disabled?: boolean;
	feedback?: string | string[];
	hint?: string;
	id?: string;
	label: string;
	labelHidden?: boolean;
	loading?: boolean;
	placeholder?: string;
	required?: boolean;
	status?: string;
}

export interface SearchSelectOptionProps {
	value: string;
	label: string;
	isFixed?: boolean;
}

export type onChangeSearchSelect = OnChangeValue<
	SearchSelectOptionProps,
	boolean
>;
export type Actions = ActionMeta<SearchSelectOptionProps>;

export interface Props extends CommonFormElementProps {
	clearable?: boolean;
	multiselect?: boolean;
	value?: SearchSelectOptionProps | SearchSelectOptionProps[] | null;
	onChange: (value: onChangeSearchSelect, action?: Actions) => void;
	onKeyDown?: (event: KeyboardEvent) => void;
	menuIsOpen?: boolean;
	dropdownIndicator?: boolean;
	onInputChange?: Dispatch<SetStateAction<string>>;
	inputValue?: string;
	onBlur?: () => void;
	limit?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	loadPageOptions?: any;
	debounceTimeout?: number;
	searchOnValue?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	cacheUniqs?: any[];
	onMenuClose?: () => void;
	footerButtons?: ReactNode;
}

export default function SearchSelectAsyncPaginate({
	className,
	dirty,
	disabled,
	feedback,
	hint,
	id,
	label,
	labelHidden,
	loading,
	placeholder,
	required,
	status,
	clearable,
	multiselect,
	value,
	onChange,
	onKeyDown,
	menuIsOpen,
	dropdownIndicator = true,
	onInputChange,
	inputValue,
	onBlur,
	limit,
	loadPageOptions,
	debounceTimeout,
	searchOnValue = false,
	cacheUniqs,
	onMenuClose,
	footerButtons,
}: Props) {
	// styles for fixed options
	const styles = useMemo(() => {
		return {
			multiValue: (
				base: CSSObjectWithLabel,
				state: MultiValueProps<
					SearchSelectOptionProps,
					boolean,
					GroupBase<SearchSelectOptionProps>
				>
			) => {
				return state.data.isFixed
					? { ...base, backgroundColor: "gray" }
					: base;
			},
			multiValueLabel: (
				base: CSSObjectWithLabel,
				state: MultiValueProps<
					SearchSelectOptionProps,
					boolean,
					GroupBase<SearchSelectOptionProps>
				>
			) => {
				return state.data.isFixed
					? {
							...base,
							color: "white",
							paddingRight: 6,
					  }
					: base;
			},
			multiValueRemove: (
				base: CSSObjectWithLabel,
				state: MultiValueProps<
					SearchSelectOptionProps,
					boolean,
					GroupBase<SearchSelectOptionProps>
				>
			) => {
				return state.data.isFixed ? { ...base, display: "none" } : base;
			},
		};
	}, []);

	const beforeProps = useMemo(() => {
		return {
			dirty,
			label,
			required,
			hidden: labelHidden,
			id,
			limit,
		};
	}, [dirty, label, required, labelHidden, id, limit]);

	const props = useMemo(() => {
		return {
			isDisabled: disabled,
			isClearable: clearable,
			isMulti: multiselect,
			placeholder,
			value,
			className: classnames("search-select", className),
			onChange,
			classNamePrefix: "search-select",
			isLoading: loading,
			styles,
			"aria-label": label,
			menuIsOpen,
			onKeyDown,
			onInputChange,
			inputValue,
			onBlur,
			onMenuClose,
			components: !dropdownIndicator
				? {
						DropdownIndicator: () => null,
						IndicatorSeparator: () => null,
				  }
				: undefined,
			filterOption: createFilter({
				ignoreCase: true,
				ignoreAccents: true,
				trim: true,
				matchFrom: "any",
				stringify: searchOnValue
					? option => `${option.value}`
					: option => `${option.label}`,
			}),
		};
	}, [
		disabled,
		clearable,
		multiselect,
		placeholder,
		value,
		className,
		onChange,
		loading,
		styles,
		label,
		menuIsOpen,
		onKeyDown,
		onInputChange,
		inputValue,
		onBlur,
		onMenuClose,
		dropdownIndicator,
		searchOnValue,
	]);

	return (
		<Elem
			aria-label={label}
			className="elem-search-select"
			dirty={dirty}
			status={status}>
			<ElemBefore {...beforeProps} />
			<AsyncPaginateSelect
				{...props}
				loadOptions={loadPageOptions}
				debounceTimeout={debounceTimeout}
				cacheUniqs={cacheUniqs}
			/>
			{footerButtons ? (
				<div className="elem-search-select__row-btns">
					{footerButtons}
				</div>
			) : null}
			<ElemAfter hint={hint} feedback={feedback} />
		</Elem>
	);
}
